import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AndroidBetaModal — Collects email + name from users who want the
 * Android beta build. Google Play internal testing requires the FOTR team
 * to add each tester's email manually, so this modal gates the Google Play
 * link behind an email submission.
 */
export default function AndroidBetaModal({ open, onClose }) {
  const [name, setName]       = useState("");
  const [email, setEmail]     = useState("");
  const [device, setDevice]   = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState(null);

  const close = () => {
    setName("");
    setEmail("");
    setDevice("");
    setLoading(false);
    setSuccess(false);
    setError(null);
    onClose?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API}/beta/android`, {
        name:         name.trim(),
        email:        email.trim(),
        device_model: device.trim() || null,
      });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="android-beta-modal"
        >
          <motion.div
            className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
            onClick={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className="relative bg-white rounded-3xl max-w-md w-full p-7 shadow-2xl ring-1 ring-ink/5"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <button
              onClick={close}
              aria-label="Close"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-line hover:bg-ink/10 flex items-center justify-center transition"
              data-testid="android-modal-close"
            >
              <X size={18} className="text-ink" />
            </button>

            {success ? (
              <div className="text-center py-4" data-testid="android-success-state">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 size={32} className="text-primary" />
                </div>
                <h3 className="font-display font-black text-2xl md:text-3xl text-ink leading-tight">You're on the list.</h3>
                <p className="text-muted mt-3 leading-relaxed">
                  We just emailed <strong className="text-ink">{email}</strong>. We'll add you to Google Play's internal testers (usually within 24 hours) and send you the install link.
                </p>
                <button
                  onClick={close}
                  className="mt-6 bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-primary-600 transition w-full"
                  data-testid="android-success-close"
                >
                  Got it
                </button>
              </div>
            ) : (
              <>
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-primary mb-3">
                  <Play size={12} fill="currentColor" /> Android Beta Access
                </div>
                <h3 className="font-display font-black text-2xl md:text-3xl text-ink leading-tight mb-2">
                  Get on the Android beta list.
                </h3>
                <p className="text-muted text-sm leading-relaxed mb-5">
                  Google Play requires us to add your email manually before you can install. Drop it here and we'll approve you within 24 hours.
                </p>

                <form onSubmit={submit} className="space-y-3" data-testid="android-signup-form">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={loading}
                    className="w-full px-4 py-3 bg-surface-alt border border-line rounded-xl text-ink placeholder:text-muted focus:outline-none focus:border-primary transition"
                    data-testid="android-input-name"
                  />
                  <input
                    type="email"
                    placeholder="Your Google / Android email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="w-full px-4 py-3 bg-surface-alt border border-line rounded-xl text-ink placeholder:text-muted focus:outline-none focus:border-primary transition"
                    data-testid="android-input-email"
                  />
                  <input
                    type="text"
                    placeholder="Device model (optional, e.g. Pixel 8, Galaxy S24)"
                    value={device}
                    onChange={(e) => setDevice(e.target.value)}
                    disabled={loading}
                    className="w-full px-4 py-3 bg-surface-alt border border-line rounded-xl text-ink placeholder:text-muted focus:outline-none focus:border-primary transition"
                    data-testid="android-input-device"
                  />

                  {error && (
                    <div className="text-sm text-primary bg-primary/5 border border-primary/20 px-4 py-2.5 rounded-lg" data-testid="android-error">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary text-white px-6 py-3.5 rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-primary-600 transition disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="android-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Sending...
                      </>
                    ) : (
                      <>Send me the install link</>
                    )}
                  </button>
                  <p className="text-xs text-muted text-center pt-1">
                    We only use your email to add you to Google Play testers. No spam.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
